import { Vector3 } from 'three'
import { PullDirectionType } from '@Admin/AdminQuotingPage/ModelViewerPanel/PanelControls/PullDirectionMenu'

import FictivSymbol from '~/lib/FictivSymbol'
import { DfmFeaturesTypes } from '~/redux-modules/qmr-viewer/constants'

import {
    AnnotationType,
    CameraConfigType,
    CrossSectionViewType,
    DfmSnapshotType,
    P2PPointType,
    PatchClickEventType,
    ViewerInteractionModeType,
    ViewerUnitsType,
} from './types'

/* Viewer handle */
export const INSTANTIATE_VIEWER = FictivSymbol('qmr-viewer/INSTANTIATE_VIEWER')

/* Viewer Controls */
export const RESET_STATE = FictivSymbol('qmr-viewer/RESET_STATE')
export const SET_TRANSPARENCY = FictivSymbol('qmr-viewer/SET_TRANSPARENCY')
export const SET_RAW_INSPECTION = FictivSymbol('qmr-viewer/SET_RAW_INSPECTION')
export const SET_PDF_INSPECTION = FictivSymbol('qmr-viewer/SET_PDF_INSPECTION')
export const SET_VIEWER_INTERACTION_MODE = FictivSymbol(
    'qmr-viewer/SET_VIEWER_INTERACTION_MODE',
)
export const SET_ACTIVE_DFM_FEATURES = FictivSymbol(
    'qmr-viewer/SET_ACTIVE_DFM_FEATURES',
)
export const SET_TEXTURE_THRESHOLD = FictivSymbol(
    'qmr-viewer/SET_TEXTURE_THRESHOLD',
)
export const SET_SELECTED_PULL_DIRECTION = FictivSymbol(
    'qmr-viewer/SET_SELECTED_PULL_DIRECTION',
)
export const SET_MEASUREMENT_UNITS = FictivSymbol(
    'qmr-viewer/SET_MEASUREMENT_UNITS',
)

export const SET_DISPLAY_MODE = FictivSymbol('qmr-viewer/SET_DISPLAY_MODE')

export const SET_TOOL_VISIBILITY = FictivSymbol(
    'qmr-viewer/SET_TOOL_VISIBILITY',
)

/* Hole/hole group interactions */
export const HOVER_HOLE_GROUP = FictivSymbol('qmr-viewer/HOVER_HOLE_GROUP')
export const HOVER_HOLE = FictivSymbol('qmr-viewer/HOVER_HOLE')

/* Dfm card interactions */
export const HOVER_DFM_CARD = FictivSymbol('qmr-viewer/HOVER_DFM_CARD')
export const CLICK_DFM_CARD = FictivSymbol('qmr-viewer/CLICK_DFM_CARD')

/* Comment Interactions */
export const COMMENT_HOVER = FictivSymbol('qmr-viewer/COMMENT_HOVER')
export const COMMENT_SELECT = FictivSymbol('qmr-viewer/COMMENT_SELECT')
export const COMMENT_CANCEL = FictivSymbol('qmr-viewer/COMMENT_CANCEL')

/* Cross Section Control */

export const SET_CROSS_SECTION_VIEW = FictivSymbol(
    'qmr-viewer/SET_CROSS_SECTION_VIEW',
)
export const SET_INTERACTION_MODE_COLOR = FictivSymbol(
    'qmr-viewer/SET_INTERACTION_MODE_COLOR',
)
export const HOVER_CROSS_SECTION_TOOL = FictivSymbol(
    'qmr-viewer/HOVER_CROSS_SECTION_TOOL',
)
export const CLEAR_CAMERA_CONFIG = FictivSymbol(
    'qmr-viewer/CLEAR_CAMERA_CONFIG',
)

/* Viewer interactions */
export const HOVER_POINT = FictivSymbol('qmr-viewer/HOVER_POINT')
export const POINT_LEFT_CLICK = FictivSymbol('qmr-viewer/POINT_LEFT_CLICK')
export const HOVER_PATCH = FictivSymbol('qmr-viewer/HOVER_PATCH')
export const PATCH_CLICK = FictivSymbol('qmr-viewer/PATCH_CLICK')
export const PATCH_CTRL_CLICK = FictivSymbol('qmr-viewer/PATCH_CTRL_CLICK')
export const PATCH_RIGHT_CLICK = FictivSymbol('qmr-viewer/PATCH_RIGHT_CLICK')
export const ANNOTATE_PATCH = FictivSymbol('qmr-viewer/ANNOTATE_PATCH')
export const RESET_ANNOTATION_PINS = FictivSymbol(
    'qmr-viewer/RESET_ANNOTATION_PINS',
)
export const PAN_CAMERA = FictivSymbol('qmr-viewer/PAN_CAMERA')
export const ZOOM_CAMERA = FictivSymbol('qmr-viewer/ZOOM_CAMERA')
export const ROTATE_CAMERA = FictivSymbol('qmr-viewer/ROTATE_CAMERA')
export const RESET_VIEW = FictivSymbol('qmr-viewer/RESET_VIEW')
export const CLICK_ANNOTATION = FictivSymbol('qmr-viewer/CLICK_ANNOTATION')
export const ESC_KEY_UP = FictivSymbol('qmr-viewer/ESC_KEY_UP')
export const CROSS_SECTION_TOOL_CLICK = FictivSymbol(
    'qmr-viewer/CLICK_CROSS_SECTION_TOOL',
)
export const MULTI_PATCH_HOVER = FictivSymbol('qmr-viewer/MULTI_PATCH_HOVER')
export const MULTI_PATCH_CLICK = FictivSymbol('qmr-viewer/MULTI_PATCH_CLICK')
export const TOGGLE_DEFAULT_DFM_SELECTION = FictivSymbol(
    'qmr-viewer/TOGGLE_DEFAULT_DFM_SELECTION',
)
export const TOGGLE_HIDE_XSECTION_TOOLS = FictivSymbol(
    'qmr-viewer/TOGGLE_HIDE_XSECTION_TOOLS',
)

// Should get the type from the viewer package, but need declaration files to work properly
export function createViewerInstance({ viewer }: { viewer: any }) {
    return {
        type: INSTANTIATE_VIEWER,
        viewer,
    }
}

export function clearCameraConfig() {
    return {
        type: CLEAR_CAMERA_CONFIG,
    }
}

export function hoverComment(commentId: string) {
    return {
        type: COMMENT_HOVER,
        commentId,
    }
}

export function cancelComment() {
    return { type: COMMENT_CANCEL }
}

export function selectComment(commentId: string) {
    return {
        type: COMMENT_SELECT,
        commentId,
    }
}

const DEFAULT_TOOL_VISIBILITY = {
    infoBar: true,
    interactionControlBox: true,
    measurePanel: true,
}

export interface ToolVisibilityType {
    infoBar?: boolean
    interactionControlBox?: boolean
    measurePanel?: boolean
}

// Handles visibility of tools on top of the ModelViewer
export const setToolVisibility = (toolVisibility: ToolVisibilityType) => {
    return {
        type: SET_TOOL_VISIBILITY,
        toolVisibility: { ...DEFAULT_TOOL_VISIBILITY, ...toolVisibility },
    }
}

export function setAnnotatable(annotatable: boolean) {
    return {
        type: SET_DISPLAY_MODE,
        annotatable,
    }
}

export function setTransparency(transparent: boolean) {
    return {
        type: SET_TRANSPARENCY,
        transparent,
    }
}

export function setInteractionModeColor(color: string) {
    return {
        type: SET_INTERACTION_MODE_COLOR,
        color,
    }
}

export function hoverCrossSectionTool(toolName: string) {
    return {
        type: HOVER_CROSS_SECTION_TOOL,
        toolName,
    }
}

export function setCrossSectionView(crossSectionView: CrossSectionViewType) {
    return {
        type: SET_CROSS_SECTION_VIEW,
        crossSectionView,
    }
}

export function setRawInspection(visible: boolean) {
    return {
        type: SET_RAW_INSPECTION,
        visible,
    }
}

export function setPdfInspection(visible: boolean) {
    return {
        type: SET_PDF_INSPECTION,
        visible,
    }
}

export function setViewerInteractionMode({
    mode,
    annotatable,
    selectAllDfmByDefault,
}: {
    mode: ViewerInteractionModeType
    annotatable: boolean
    selectAllDfmByDefault: boolean
}) {
    return {
        type: SET_VIEWER_INTERACTION_MODE,
        mode,
        annotatable,
        selectAllDfmByDefault,
    }
}
export function setActiveDfmFeatures(
    features: DfmFeaturesTypes[],
    visible: boolean,
) {
    return {
        type: SET_ACTIVE_DFM_FEATURES,
        features,
        visible,
    }
}
export function setTextureThreshold(threshold: number | null) {
    return {
        type: SET_TEXTURE_THRESHOLD,
        threshold,
    }
}
export function setSelectedPullDirection(
    selectedPullDirection: PullDirectionType,
) {
    return {
        type: SET_SELECTED_PULL_DIRECTION,
        selectedPullDirection,
    }
}
export function setMeasurementUnits(units: ViewerUnitsType) {
    return {
        type: SET_MEASUREMENT_UNITS,
        units,
    }
}

export function hoverPatch(patchHash: string | null) {
    return { type: HOVER_PATCH, patchHash }
}

export function patchClick(patchClickEvent: PatchClickEventType) {
    return { type: PATCH_CLICK, patchClickEvent }
}

export function patchCtrlClick(patchClickEvent: PatchClickEventType) {
    return { type: PATCH_CTRL_CLICK, patchClickEvent }
}

export function patchRightClick(patchClickEvent: PatchClickEventType) {
    return { type: PATCH_RIGHT_CLICK, patchClickEvent }
}

export const resetAnnotationPins = () => {
    return { type: RESET_ANNOTATION_PINS }
}

export function hoverPoint(
    pointIndex: number | null,
    pointType: P2PPointType | null,
) {
    return { type: HOVER_POINT, pointIndex, pointType }
}

export function pointLeftClick(pointIndex: number, pointType: P2PPointType) {
    return { type: POINT_LEFT_CLICK, pointIndex, pointType }
}

export function annotatePatch(patchClickEvent: PatchClickEventType) {
    return {
        type: ANNOTATE_PATCH,
        patchClickEvent,
    }
}

export function hoverHole({ holeId }: { holeId: string }) {
    return {
        type: HOVER_HOLE,
        holeId: holeId || null,
    }
}

export function hoverHoleGroup({ holeGroupId }: { holeGroupId: string }) {
    return {
        type: HOVER_HOLE_GROUP,
        groupId: holeGroupId || null,
    }
}

export function hoverDfmCard({ dfmId }: { dfmId: string | null }) {
    return {
        type: HOVER_DFM_CARD,
        dfmId: dfmId || null,
    }
}

export function clickDfmCard({
    dfmId,
    dfmSnapshot,
}: {
    dfmId: string
    dfmSnapshot: DfmSnapshotType | null
}) {
    return {
        type: CLICK_DFM_CARD,
        dfmId: dfmId || null, // typescript needs help
        dfmSnapshot: dfmSnapshot || null,
    }
}

export function panCamera(newCameraConfig: CameraConfigType) {
    return {
        type: PAN_CAMERA,
        newCameraConfig,
    }
}

export function zoomCamera(newCameraConfig: CameraConfigType) {
    return {
        type: ZOOM_CAMERA,
        newCameraConfig,
    }
}

export function rotateCamera(newCameraConfig: CameraConfigType) {
    return {
        type: ROTATE_CAMERA,
        newCameraConfig,
    }
}

export function resetView() {
    return {
        type: RESET_VIEW,
    }
}

export function clickAnnotation({
    annotation,
}: {
    annotation: AnnotationType
}) {
    return {
        type: CLICK_ANNOTATION,
        annotation,
    }
}

export function clickCrossSectionTool({
    normal,
    pointCoordinates,
}: {
    normal: Vector3
    pointCoordinates: Vector3
}) {
    return {
        type: CROSS_SECTION_TOOL_CLICK,
        normal,
        pointCoordinates,
    }
}

export function escKeyUp() {
    return {
        type: ESC_KEY_UP,
    }
}

export function multiplePatchHover(patchHashes: string[]) {
    return {
        type: MULTI_PATCH_HOVER,
        patchHashes,
    }
}

export function multiplePatchClick(patchHashes: string[]) {
    return {
        type: MULTI_PATCH_CLICK,
        patchHashes,
    }
}

export function toggleDefaultDfmSelection() {
    return {
        type: TOGGLE_DEFAULT_DFM_SELECTION,
    }
}

export function toggleHideCrossSectionTools() {
    return {
        type: TOGGLE_HIDE_XSECTION_TOOLS,
    }
}

export function resetViewerState() {
    return {
        type: RESET_STATE,
    }
}

export type ViewerActionType =
    | ReturnType<typeof createViewerInstance>
    | ReturnType<typeof clearCameraConfig>
    | ReturnType<typeof hoverComment>
    | ReturnType<typeof cancelComment>
    | ReturnType<typeof selectComment>
    | ReturnType<typeof setAnnotatable>
    | ReturnType<typeof setTransparency>
    | ReturnType<typeof setInteractionModeColor>
    | ReturnType<typeof hoverCrossSectionTool>
    | ReturnType<typeof setCrossSectionView>
    | ReturnType<typeof setRawInspection>
    | ReturnType<typeof setPdfInspection>
    | ReturnType<typeof setViewerInteractionMode>
    | ReturnType<typeof setActiveDfmFeatures>
    | ReturnType<typeof setTextureThreshold>
    | ReturnType<typeof setSelectedPullDirection>
    | ReturnType<typeof setMeasurementUnits>
    | ReturnType<typeof hoverPatch>
    | ReturnType<typeof patchClick>
    | ReturnType<typeof patchCtrlClick>
    | ReturnType<typeof patchRightClick>
    | ReturnType<typeof hoverPoint>
    | ReturnType<typeof pointLeftClick>
    | ReturnType<typeof annotatePatch>
    | ReturnType<typeof hoverHole>
    | ReturnType<typeof hoverHoleGroup>
    | ReturnType<typeof hoverDfmCard>
    | ReturnType<typeof clickDfmCard>
    | ReturnType<typeof panCamera>
    | ReturnType<typeof zoomCamera>
    | ReturnType<typeof rotateCamera>
    | ReturnType<typeof resetView>
    | ReturnType<typeof clickAnnotation>
    | ReturnType<typeof clickCrossSectionTool>
    | ReturnType<typeof escKeyUp>
    | ReturnType<typeof multiplePatchHover>
    | ReturnType<typeof multiplePatchClick>
    | ReturnType<typeof toggleDefaultDfmSelection>
    | ReturnType<typeof toggleHideCrossSectionTools>
    | ReturnType<typeof resetViewerState>
    | ReturnType<typeof setToolVisibility>
    | ReturnType<typeof resetAnnotationPins>
